import {cdnImage} from '@/util/cdnImage';
import Ganache from 'public/images/technologies/Ganache.webp';
import Alchemy from 'public/images/technologies/alchemy.webp';
import Infura from 'public/images/technologies/infura.webp';
import Coinbase from 'public/images/technologies/coinbase.webp';
import Hardhat from 'public/images/technologies/hardhat.webp';
import Truffle from 'public/images/technologies/truffle.webp';
import Vyper from 'public/images/technologies/vyper.webp';
import etherjs from 'public/images/technologies/etherjs.webp';
import web3js from 'public/images/technologies/web3js.webp';
import html5 from 'public/images/technologies/HTML5.webp';
import metamask from 'public/images/technologies/metamask.webp';
export const defiFAQData = [
    {
        category: 'General DeFi',
        questions: [
            {
                q: 'What does DeFi development do?',
                a: 'DeFi development involves building decentralized financial applications on blockchain networks that operate without traditional intermediaries. Through smart contracts, these platforms automate financial processes including lending, borrowing, trading, and yield generation — executing transactions transparently and in real time. For businesses, this translates to reduced settlement costs, eliminated counterparty risk, and access to global liquidity pools.'
            },
            {
                q: 'Which blockchain networks do you support for DeFi development?',
                a: "We develop DeFi solutions across Ethereum, BNB Chain, Solana, Polygon, Avalanche, Arbitrum, Optimism, Polkadot, and Base. Network selection is driven by your protocol's throughput requirements, transaction cost tolerance, composability needs, and scalability roadmap — not a default preference. For <a href='/blog/top-defi-protocols' target='_blank'>DeFi protocols</a> requiring multi-ecosystem access, we architect cross-chain solutions using established bridge frameworks that maintain security without compromising performance."
            },
            {
                q: 'How do you ensure the security of DeFi smart contracts?',
                a: 'Security is embedded into every development stage, not reviewed at the end. Smart contracts are continuously tested throughout development and undergo a multi-layered audit before mainnet deployment — combining automated analysis via Slither, MythX, and Echidna with manual review by senior <a href="/hire-smart-contract-developers" target="_blank">smart contract engineers</a>. We audit for reentrancy, access control flaws, oracle manipulation, integer overflow, and flash loan vulnerabilities. All findings are fully remediated before deployment proceeds.'
            },
            {
                q: 'Can you build a DeFi platform that is compliant with SEC, GDPR, and AML regulations?',
                a: 'Yes. Before development begins, we map the full compliance perimeter applicable to your platform — covering SEC token classification requirements, AML and KYC obligations, GDPR and CCPA data handling standards, and SOX audit trail requirements where applicable. Compliance logic is embedded directly into smart contract design and data architecture from the outset, not retrofitted at launch.'
            },
            {
                q: 'What is the difference between building on an existing DeFi protocol and developing a custom DeFi solution?',
                a: 'Building on an existing protocol — such as forking Uniswap or Aave — offers faster deployment with battle-tested base-layer code, suited for use cases that align closely with established protocol mechanics. Custom DeFi development gives full control over protocol logic, tokenomics, and governance architecture, and is the right choice when your use case or compliance requirements cannot be served by existing templates. We evaluate both paths against your business model and recommend accordingly.'
            },
            {
                q: 'Do you provide smart contract auditing as part of your DeFi development service?',
                a: 'Yes. Smart contract auditing is a standard component of every engagement, not an optional add-on. Our audit process combines automated static analysis with manual review by senior smart contract security engineers, covering the full spectrum of known DeFi vulnerability classes. For protocols managing significant on-chain liquidity or with institutional security requirements, we additionally facilitate independent third-party audits before mainnet deployment.'
            },
            {
                q: 'Can you integrate our DeFi platform with existing enterprise systems such as ERP, treasury, or CRM software?',
                a: 'Yes. We develop custom APIs that connect DeFi application layers to ERP platforms, treasury management systems, CRM software, and corporate finance infrastructure. When real-time external data is required within smart contract logic, we build and integrate custom oracle solutions that securely bridge on-chain and off-chain environments. Every integration is designed with data integrity, latency tolerance, and failure recovery in mind.'
            },
            {
                q: 'How do you handle smart contract upgrades after the DeFi platform has gone live?',
                a: "Upgradeability is planned before development begins, not after. We architect upgrade pathways into the protocol from the outset using proxy contracts, modular contract design, and governance-controlled upgrade mechanisms. Post-deployment, all upgrade proposals go through a defined review, testing, and re-audit cycle before execution — ensuring changes are transparent, secure, and aligned with the protocol's governance framework."
            },
            {
                q: 'Do I need both a token and an exchange for my DeFi project?',
                a: 'If you are planning to launch a scalable DeFi product, combining token creation with an exchange layer can significantly strengthen your business model. Through DeFi token development, you can establish monetization, incentives, and governance mechanisms, while DeFi exchange development enables trading, liquidity access, and user participation. Partnering with an experienced team ensures both components are designed for security, performance, and long-term growth.'
            },
            {
                q: 'What ongoing support and maintenance do you offer after DeFi platform deployment?',
                a: 'Our post-deployment engagement covers real-time protocol and infrastructure monitoring, rapid incident response, regular security and compliance audits, and infrastructure scaling as transaction volume grows. We also continue to evolve the protocol with new asset support, additional service layers such as staking and decentralized insurance, and cross-chain expansion — ensuring the platform remains secure and competitive over time.'
            }
        ]
    }
];

export const trendData = [
    {
        subheading: 'According to Mordor Intelligence, the DeFi market is expected to reach $78.47 billion by 2029.'
    },
    {
        subheading:
            'According to <a target="_blank" href="https://www.grandviewresearch.com/industry-analysis/decentralized-finance-market-report" rel="nofollow">Grand View Research</a>, the DeFi market is projected to reach $231.19 billion by 2030. '
    },
    {
        subheading:
            '<a target="_blank" href="https://www.statista.com/outlook/fmo/digital-assets/defi/worldwide#revenue" rel="nofollow">Statista</a> also anticipates ARPU in DeFi to reach around USD 1,650 by 2027, signaling deeper individual engagement.'
    },
    {
        subheading:
            '<a target="_blank" href="https://www.marketsandata.com/industry-reports/decentralized-finance-defi-market" rel="nofollow">Markets & Data</a> notes that by 2032, decentralized exchanges (DEXs) are projected to account for over 40 % of total DeFi transaction volume, highlighting shifting on-chain liquidity patterns.'
    }
];

const consulting = cdnImage('defi/DeFi Consulting Services.webp');
const token = cdnImage('defi/DeFi Token Dev.webp');
const exchange = cdnImage('defi/DeFi Exchange.webp');
const lending = cdnImage('defi/DeFi Lending.webp');
const funding = cdnImage('defi/DeFi Crowdfunding.webp');
const smartContract = cdnImage('defi/DeFi Smart Contract.webp');
const wallet = cdnImage('defi/DeFi Wallet.webp');
const insurance = cdnImage('defi/DeFi Insurance.webp');
const staking = cdnImage('defi/DeFi Staking.webp');
const restaking = cdnImage('defi/DeFi Restaking.webp');
const farming = cdnImage('defi/DeFi Yield Farming.webp');
const platform = cdnImage('defi/DeFi Derivatives.webp');
const haiderImg = cdnImage('Blockchain-images/haider-rafique.webp');
const dexPlatforms = cdnImage('defi/Decentralized-Exchange-Platforms.webp');
const lendingPlatforms = cdnImage('defi/DeFi-Lending-Borrowing-Platforms.webp');
const stakingPlatforms = cdnImage('defi/DeFi-Staking-Restaking-Platforms.webp');
const fundingPlatforms = cdnImage('defi/Decentralized-Crowdfunding-Platforms.webp');
const farmingPlatforms = cdnImage('defi/Yield-Farming-Liquidity-Protocol-Platforms.webp');
const derivativesPlatforms = cdnImage('defi/Decentralized-Derivatives-Trading-Platforms.webp');
const walletDev = cdnImage('defi/DeFi-Wallet-Development.webp');
const bridgeSolutions = cdnImage('defi/Cross-Chain-Bridging-Solutions.webp');
const assetTokenization = cdnImage('defi/Asset-Tokenization-Platforms.webp');
const auditSmartContract = cdnImage('defi/DeFi-Smart-Contract-Development-Auditing.webp');
const elevenYear = cdnImage('blockchain-technology/11year.webp');
const ibm1 = cdnImage('blockchain-technology/ibm.webp');
const fabdep = cdnImage('blockchain-technology/fabdep.webp');
const defiProtocolCtaImg = cdnImage('defi/Defi-Protocol-Cta.webp');
const defiFinalCtaGraphic = cdnImage('defi/DeFi-Solution-cta.webp');
export const defiDevelopmentData = [
    {
        icon: consulting,
        heading: 'DeFi Consulting Services',
        redirect: '',
        body: `Our consulting experts guide you through decentralized finance with clarity and precision. We uncover opportunities, address risks, and align strategies with business goals. With skilled DeFi developers on board, your project gains the expertise needed to build impactful, future-ready financial ecosystems.`
    },
    {
        icon: token,
        heading: 'DeFi Token Development',
        redirect: '',
        body: `We build high-utility tokens that strengthen engagement, liquidity, and governance across platforms. These tokens provide long-term value and align with user needs. Our structured process ensures your token seamlessly integrates within your DeFi platform, delivering trust, growth, and sustainable adoption.`
    },
    {
        icon: exchange,
        heading: 'DeFi Exchange Development',
        redirect: '',
        body: `We design <a target="_blank" href="/decentralized-crypto-exchange-development">decentralized exchanges</a> that enhance transparency and liquidity while eliminating intermediaries. Built with automated smart contracts, these exchanges empower users with secure, efficient trading. Our approach ensures reliability, making your project stand out in the competitive DeFi landscape.`
    },
    {
        icon: lending,
        heading: 'DeFi Lending and Borrowing Platform',
        redirect: '/defi-lending-platform-development',
        body: `We create lending platforms that automate loans through secure smart contracts. These systems reduce risks, cut costs, and enhance global accessibility. Designed for convenience and safety, they provide flexible borrowing and lending opportunities while ensuring smooth user experiences across decentralized finance services.`
    },
    {
        icon: funding,
        heading: 'DeFi Crowdfunding Development',
        redirect: '',
        body: `Our crowdfunding platforms enable projects to raise funds through blockchain-powered models like IDOs. Designed for transparency and trust, these solutions foster engaged investor communities. We ensure secure fundraising, empowering innovators to achieve market visibility and attract long-term backers in decentralized finance.`
    },
    {
        icon: smartContract,
        heading: 'DeFi Smart Contract Development',
        redirect: '',
        body: `Our DeFi <a target="_blank" href="/smart-contract-development">smart contract development services</a> ensure reliability, transparency, and automation in financial operations. Each contract undergoes thorough testing to eliminate vulnerabilities and secure user funds. With tamper-proof functionality, our contracts enhance credibility while reducing risks within your decentralized financial ecosystem.`
    },
    {
        icon: wallet,
        heading: 'DeFi Wallet Development',
        redirect: '',
        body: `Through DeFi wallet development, we design secure, intuitive wallets that support multiple assets. With strong encryption and seamless usability, these wallets empower users to manage crypto efficiently. Our DeFi wallet app development services ensure accessibility, making wallets a vital part of your financial ecosystem.`
    },
    {
        icon: insurance,
        heading: 'DeFi Insurance Platform',
        redirect: '',
        body: `We build decentralized insurance solutions that safeguard user assets from risks like hacks and volatility. By enabling transparent claims and customizable policies, these platforms strengthen trust. Users feel more confident investing, knowing that additional protection supports their decentralized finance activities.`
    },
    {
        icon: staking,
        heading: 'DeFi Staking Platform',
        redirect: '/defi-staking-platform-development',
        body: `Our <a target="_blank" href="/blog/best-defi-staking-platforms">staking platforms</a> help users earn rewards by locking digital assets securely. Designed with scalability, automation, and simple interfaces, they encourage active participation. By increasing liquidity and engagement, staking solutions become essential for strengthening user retention within decentralized ecosystems.`
    },
    {
        icon: restaking,
        heading: 'DeFi Restaking Platform',
        redirect: '',
        body: `We develop restaking platforms that maximize the utility of previously staked assets. These systems enhance capital efficiency, boost rewards, and offer additional opportunities. By integrating flexible mechanisms, our solutions promote sustainable user engagement and long-term growth in decentralized financial platforms.`
    },
    {
        icon: farming,
        heading: 'DeFi Yield Farming Platform',
        redirect: '/blog/yield-farming-platforms',
        body: `Our yield farming solutions empower users to grow assets with liquidity pools and reward models. Designed for transparency, automation, and security, they attract active investors. Yield farming ensures projects engage communities while delivering sustainable returns through decentralized participation.`
    },
    {
        icon: platform,
        heading: 'DeFi Derivatives Platform',
        redirect: '',
        body: `We design <a target="_blank" href="/blog/cryptocurrency-derivatives-exchange-platforms">derivatives platforms</a> for decentralized trading of futures, options, and synthetics. Secure, scalable, and intuitive, these platforms expand investment opportunities. By integrating user-friendly interfaces, they strengthen accessibility while positioning your business as a leader in decentralized financial innovation.`
    }
];

export const ndaxDynamicScreenButton = {
    header: 'An Innovative Blockchain Wallet & Portfolio Management Solution',
    button: ['Blockchain', 'AI', 'Advanced Algorithmic Trading']
};

export const landmateDynamicScreenButton = {
    header: 'A Trusted Platform for Effortless and Secure Peer-to-Peer Loans',
    button: ['Blockchain', ' Solidity', 'Node.js', 'Docker']
};

export const DXDynamicScreenButton = {
    header: 'An Ultimate Hub for Secure, Seamless Crypto Trading and Exploration',
    button: ['Ethereum', 'Vyper', 'React.js', 'MongoDB']
};

export const nexusScreenButton = {
    header: 'An Innovative Platform for Secure and Efficient NFT Trading and Management',
    button: ['Ethereum', 'Solidity', 'Vue.js', 'PostgreSQL']
};

export const defiCarsoulData = [
    {
        name: 'Bilal Hammoud',
        discrption: `Working with Debut Infotech has been a game-changer for NDAX. Their unparalleled expertise in blockchain and fintech development allowed us to build a seamless, robust platform that enhances user experience and security. Their team's dedication, innovative approach, and attention to detail were evident throughout the project. Debut Infotech consistently exceeded our expectations, ensuring NDAX's position as a leading crypto trading platform. I highly recommend Debut Infotech to any enterprise seeking top-notch development services.`,
        profile: 'Founder of NDAX',
        src: cdnImage('defi/Bilal-Hammoud.webp'),
        isVideo: false,
        link: ''
    },
    {
        name: 'Alexander Bennett',

        discrption: `Collaborating with Debut Infotech to develop LendMate was an exceptional experience. Their expertise in blockchain and peer-to-peer lending platforms enabled us to create a secure, efficient, and user-friendly solution. Their innovative approach and dedication have significantly enhanced our operations, ensuring a seamless experience for our users. Debut Infotech's commitment to excellence has positioned LendMate as a leading platform in the industry. I highly recommend their services to anyone seeking top-tier development and unmatched support.`,
        profile: 'CEO of LendMate',
        src: cdnImage('defi/Alexander-Bennett.webp'),
        isVideo: false,
        link: ''
    },
    {
        name: 'Michael Stevens',
        discrption:
            "Debut Infotech's expertise in blockchain technology and crypto exchange development has been pivotal for DebutDX. Their innovative approach and unwavering dedication have created a secure and efficient platform that stands out in the market. I highly recommend their services to anyone looking to develop a cutting-edge crypto exchange.",
        profile: 'Co-Founder of DebutDX',
        src: cdnImage('defi/Michael-Stevens.webp'),
        isVideo: false,
        link: ''
    },
    {
        name: 'Sarah Mitchell',
        discrption: `Debut Infotech's expertise in building the DebutCX platform has been transformative for our business. Their commitment to quality, security, and innovative solutions has resulted in a top-tier exchange platform that meets all our operational needs. I highly recommend their services for anyone looking to develop a robust crypto exchange.`,
        profile: 'Founder of NexusNFT',
        src: cdnImage('defi/Sarah-Mitchell.webp'),
        isVideo: false,
        link: ''
    }
];

const chain = cdnImage('defi/Interoperability.webp');
const transaction = cdnImage('defi/Pseudonymous Transactions.webp');
const implement = cdnImage('defi/Smart Contract Implementation.webp');
const goverence = cdnImage('defi/Decentralized Governance.webp');
const liquidity = cdnImage('defi/Liquidity Provision.webp');
const security = cdnImage('defi/Security Protocols.webp');
const friendly = cdnImage('defi/User-Friendly Interface.webp');
const farmingStaking = cdnImage('defi/Yield Farming.webp');
const transparent = cdnImage('defi/Auditability.webp');
const MultiAsset = cdnImage('defi/Multi-Asset-Wallet-Integration.webp');
const ScalableEnterprise = cdnImage('defi/Scalable-Enterprise-Solutions.webp');
const AdvancedLending = cdnImage('defi/Advanced-Lending-Modules.webp');
export const keyFeatureDefiContent = [
    {
        icon: chain,
        alt: 'Cross-Chain Interoperability',
        heading: 'Cross-Chain Interoperability',
        redirect: '',
        body: 'We enable seamless transactions and interactions across different blockchain networks, allowing users to access diverse assets and functionalities on a single platform.'
    },
    {
        icon: transaction,
        alt: 'Pseudonymous Transactions',
        heading: 'Pseudonymous Transactions',
        redirect: '',
        body: `We protect user privacy by facilitating transactions that don't require personal identity disclosure, ensuring secure and anonymous interactions on the platform.`
    },
    {
        icon: implement,
        alt: 'Smart Contract Implementation',
        heading: 'Smart Contract Implementation',
        redirect: '',
        body: `We automate processes with smart contracts, which execute agreements securely and without intermediaries, enhancing efficiency and trust in financial transactions.`
    },
    {
        icon: goverence,
        alt: 'Decentralized Governance',
        heading: 'Decentralized Governance',
        redirect: '',
        body: `We empower our community to participate in platform decisions through decentralized voting mechanisms, ensuring that all voices are heard and valued.`
    },
    {
        icon: liquidity,
        alt: 'Liquidity Provision',
        heading: 'Liquidity Provision',
        redirect: '',
        body: `We provide liquidity pools that allow users to trade assets easily and efficiently, maintaining a healthy and active market for all participants.`
    },
    {
        icon: security,
        alt: 'Security Protocols',
        heading: 'Security Protocols',
        redirect: '',
        body: `We implement robust security measures to safeguard the platform from hacks and vulnerabilities, ensuring the safety of user assets and data.`
    },
    {
        icon: friendly,
        alt: 'User-Friendly Interface',
        heading: 'User-Friendly Interface',
        redirect: '',
        body: "We design intuitive and accessible interfaces, making it easy for users of all experience levels to navigate and utilize the platform's features."
    },
    {
        icon: farmingStaking,
        alt: 'Yield Farming and Staking',
        heading: 'Yield Farming and Staking',
        redirect: '',
        body: `We offer opportunities for users to earn rewards by staking tokens or providing liquidity, incentivizing active participation in the ecosystem.`
    },
    {
        icon: transparent,
        alt: 'Transparent Auditability',
        heading: 'Transparent Auditability',
        redirect: '',
        body: `We ensure transparency by making all transactions and smart contract codes publicly accessible, allowing users to verify and trust the platform's operations.`
    },
    {
        icon: MultiAsset,
        alt: 'Multi-Asset Wallet Integration',
        heading: 'Multi-Asset Wallet Integration',
        redirect: '',
        body: 'We create wallets that allow users to manage, transact, and store multiple digital currencies seamlessly within the ecosystem.'
    },
    {
        icon: ScalableEnterprise,
        alt: 'Scalable Enterprise Solutions',
        heading: 'Scalable Enterprise Solutions',
        redirect: 'Scalable Enterprise Solutions',
        body: 'We deliver features that support large-scale operations, enabling businesses to adopt decentralized finance with reliability, compliance, and enterprise-grade security.'
    },
    {
        icon: AdvancedLending,
        alt: 'Advanced Lending Modules',
        heading: 'Advanced Lending Modules',
        redirect: '',
        body: 'We integrate lending modules inspired by top-performing platforms, allowing users to lend or borrow assets efficiently with transparency and minimal risk exposure.'
    }
];

const versatile = cdnImage('defi/Versatile Accessibility.webp');
const business = cdnImage('defi/Scale Business.webp');
const solo = cdnImage('defi/Sole Proprietorship.webp');
const brand = cdnImage('defi/Brand Prominence.webp');
const third = cdnImage('defi/No Third Party.webp');
const guarnty = cdnImage('defi/Guaranteed Compliance.webp');
export const traditionalDefiHelperContent = [
    {
        icon: versatile,
        heading: 'Versatile Accessibility',
        redirectLink: '',
        body: `DeFi platform development services enable broad accessibility to digital assets across various entities, enhancing usability and token utility. This versatile access boosts the scalability of DeFi apps, supported by DeFi wallets, decentralized exchanges, and other DeFi development solutions.`
    },
    {
        icon: business,
        heading: 'Scale Business Globally',
        redirectLink: '',
        body: `Decentralized finance refers to the direct entities that are capable of conducting business operations without any obstacles beyond geographical borders. This is in direct alignment with the promotion of an enhanced financial status by transitioning the Cryptos to a Go Global mode of trade.`
    },
    {
        icon: solo,
        heading: 'Sole Proprietorship',
        redirectLink: '',
        body: `DeFi grants participants sole proprietorship of their finances. Decentralized finance ensures participants can track their transactions and monetize their assets independently, without central authority intervention, leveraging DeFi staking and DeFi <a target="_blank" href="/token-development-company">token development company</a> services.`
    },
    {
        icon: brand,
        heading: 'Brand Prominence',
        redirectLink: '',
        body: 'Investing in DeFi development services can help enterprises grow and enhance brand prominence. By using DeFi, companies can establish a strong market presence and attract a broad user base, with support from a DeFi development provider and a decentralized finance development company.'
    },
    {
        icon: third,
        heading: 'No Third Party Involvement',
        redirectLink: '',
        body: `Hiring a DeFi development provider means getting experts skilled in creating decentralized finance solutions. They specialize in building DeFi platforms, such as decentralized exchanges and crypto DeFi wallets, allowing direct user interactions without intermediaries for enhanced transparency and efficiency`
    },
    {
        icon: guarnty,
        heading: 'Guaranteed Compliance',
        redirectLink: '',
        body: `Our decentralized finance development company develops comprehensive strategies to ensure that DeFi solutions comply with KYC/AML regulations, ISO 27001/27002 standards, SEC, FINRA, GLBA, PCI DSS, GDPR, NYDFS, SAMA, and other relevant global, national, and industry-specific regulations.`
    }
];

const ethereum = cdnImage('Hire-team/ethereum.webp');
const hyperledgerFabric = cdnImage('Hire-Blockchain-developer/Hyperledger-Fabric.webp');
const bitcoin = cdnImage('Multicurrency-wallet-development/Bitcoin-core.webp');
const solana = cdnImage('Hire-Blockchain-developer/Solana.webp');
const aws = cdnImage('generative-ai-integration/AWS-AI-Services.webp');
const googleCloud = cdnImage('hire-remote-developer/Google-Cloud-Platform.webp');
const solidity = cdnImage('Hire-Blockchain-developer/Solidity-Coverage.webp');
const rust = cdnImage('Hire-Blockchain-developer/Rust.webp');
const brownie = cdnImage('Hire-Blockchain-developer/Brownie.webp');
const waffle = cdnImage('Hire-Blockchain-developer/Waffle.webp');
const docker = cdnImage('generative-ai-integration/Docker.webp');
const kubernetes = cdnImage('generative-ai-integration/Kubernetes.webp');
const java = cdnImage('hire-remote-developer/Java.webp');
const python = cdnImage('hybrid-crypto-exchange/Python.webp');
const nodejs = cdnImage('Hire-team/node-js.webp');
const ansible = cdnImage('hire-remote-developer/Ansible.webp');
const puppet = cdnImage('Hire-Blockchain-developer/Puppet.webp');
const clojure = cdnImage('defi/Clojure.webp');
const bamboo = cdnImage('defi/Bamboo.webp');
const php = cdnImage('defi/php.webp');
const angular = cdnImage('Hire-team/Angular.webp');
const polygon1 = cdnImage('hybrid-crypto-exchange/polygon.webp');
const cardano = cdnImage('Hire-team/Cardano.webp');
const oracle1 = cdnImage('Hire-Blockchain-developer/oracle.webp');
const azure = cdnImage('hire-remote-developer/Microsoft-Azure.webp');
const ibmCloud = cdnImage('CryptoDerivativesExchange/IBM-Cloud.webp');
const goLang = cdnImage('hybrid-crypto-exchange/Golang.webp');
const Foundry = cdnImage('DeFi-Lending-Platform-Development/Foundry.webp');
const embark = cdnImage('Hire-Blockchain-developer/Brownie 2.webp');
const dart = cdnImage('defi/Dart.webp');
const svelte = cdnImage('Hire-team/Svelte.webp');
const nuxtjs = cdnImage('Saas-Development/Nuxt-js.webp');
const ruby = cdnImage('CryptoDerivativesExchange/Ruby.webp');
const redHat = cdnImage('CryptoDerivativesExchange/Red-hat.webp');
const sapCloud = cdnImage('defi/Sap-cloud.webp');
const hashicrop = cdnImage('DAO/HashiCorp-Terraform.webp');
const chef = cdnImage('Hire-Blockchain-developer/Chef.webp');
const jenkins = cdnImage('generative-ai-integration/Jenkins.webp');
const ApacheMesos = cdnImage('defi/Apache-Mesos.webp');
const Saltstack = cdnImage('defi/Saltstack.webp');
const LXC = cdnImage('defi/LXC.webp');
const Nomad = cdnImage('defi/Nomad.webp');
const kotlin = cdnImage('Hire-team/kotlin.webp');
const arbitrum = cdnImage('defi/Arbitrum.png');
const baseStack = cdnImage('defi/Base.png');
const avalanche = cdnImage('Blockchain-images/Avalanche.webp');
const optimism = cdnImage('Hire-Smart-contract-developers/Optimism.webp');
const binance = cdnImage('Blockchain-images/BNB-Chain.webp');
const polkadotLogo = cdnImage('Blockchain-images/Polkadot.webp');
const chainlink = cdnImage('crypto-exchange/Chainlink.webp');
const pyth = cdnImage('Solana-images/Pyth-Network.webp');
const theGraph = cdnImage('hire-defi-developers/the-graph-logo.webp');
const api3 = cdnImage('Ico-development/API3.webp');
const openZeppelin = cdnImage('Hire-Blockchain-developer/Open-Zeppelin.webp');
const FoundryLogo = cdnImage('DeFi-Lending-Platform-Development/Foundry.webp');
const Move = cdnImage('DeFi-Lending-Platform-Development/Move.webp');
const Cairo = cdnImage('defi/Cairo.png');
const Yul = cdnImage('defi/Yul.webp');
const Ink = cdnImage('defi/Ink.webp');
const Remix = cdnImage('defi/Remix.png');
const Tenderly = cdnImage('web3/Tenderly.webp');
const Band = cdnImage('defi/Band.png');
const Tellor = cdnImage('defi/Tellor.png');
const Chainstack = cdnImage('defi/Chainstack.webp');
const Moralis = cdnImage('defi/Moralis.png');
const QuickNode = cdnImage('web3/QuickNode.webp');
const IPFS = cdnImage('defi/IPFS.png');
const Filecoin = cdnImage('defi/Filecoin.png');
const Arweave = cdnImage('defi/Arweave.png');
const Ceramic = cdnImage('defi/Ceramic.png');
const OrbitDB = cdnImage('defi/OrbitDB.png');
const Slither = cdnImage('Smart-contract-auditing/Slither.webp');
const Echidna = cdnImage('Smart-contract-auditing/Echidna.webp');
const Certik = cdnImage('white-label-crypto-exchage/certik.webp');
const Manticore = cdnImage('defi/Manticore.webp');
const Gnosis = cdnImage('defi/Gnosis.png');
const Web3Auth = cdnImage('web3/Web3Auth.webp');
const Privy = cdnImage('defi/Privy.webp');
const RainbowKit = cdnImage('defi/RainbowKit.webp');
const WalletConnect = cdnImage('hire-defi-developers/WalletConnect.webp');
const reactjs = cdnImage('Hire-team/React-js.webp');
const nextJs = cdnImage('Hire-team/next-js.webp');
const vuejs = cdnImage('hire-remote-developer/Vue-js.webp');
const typescript = cdnImage('hire-remote-developer/TypeScript.webp');
const javascript = cdnImage('Hire-Blockchain-developer/Javascript.webp');
const css3 = cdnImage('hire-remote-developer/CSS3.webp');
const MythX = cdnImage('CryptoDerivativesExchange/MythX.webp');
const kycAml = cdnImage('defi/Built-to-Support-KYC-AML-Integration.webp');
const permissionedPools = cdnImage('defi/Permissioned-Pools-for-Controlled-Institutional-Access.webp');
const daoLegalWrapper = cdnImage('defi/DAO-Governance-for-Legal-Wrapper.webp');
const tokenModelsRegulatory = cdnImage('defi/Defi-Token-Model-with-Regulatory.webp');
const multiJurisdiction = cdnImage('defi/Multi-Jurisdiction-Deployment-Preparedness.webp');
const govTreasury = cdnImage('defi/Governance-and-Treasury-Controls.webp');
const gdprCcpa = cdnImage('defi/Data-Handling-Aligned-with-GDPR.webp');
const auditSox = cdnImage('defi/Audit-Trails-Designed-for-SOX.webp');
const roleBaseAccess = cdnImage('defi/Role-Based-Access-and-Multi-Signature-Controls.webp');
const monitoringAnomaly = cdnImage('defi/Real-Time-Monitoring-and-Anomaly-Detection-Support.webp');
export const defiTechnologyData = [
    {
        heading: 'Blockchain Networks',
        values: [
            {icon: ethereum, label: 'Ethereum'},
            {icon: hyperledgerFabric, label: 'Hyperledger Fabric'},
            {icon: bitcoin, label: 'Bitcoin'},
            {icon: solana, label: 'Solana'},
            {icon: polygon1, label: 'Polygon'},
            {icon: cardano, label: 'Cardano'}
        ]
    },
    {
        heading: 'Blockchain Cloud Services',
        values: [
            {icon: aws, label: 'AWS'},
            {icon: oracle1, label: 'Oracle'},
            {icon: sapCloud, label: 'SAP Cloud'},
            {icon: googleCloud, label: 'Google Cloud'},
            {icon: azure, label: 'Microsoft Azure '},
            {icon: ibmCloud, label: 'IBM'}
        ]
    },
    {
        heading: 'Smart Contract Programming Languages',
        values: [
            {icon: solidity, label: 'Solidity'},
            {icon: rust, label: 'Rust'},
            {icon: clojure, label: 'Clojure'},
            {icon: bamboo, label: 'Bamboo'},
            {icon: Vyper, label: 'Vyper'},
            {icon: goLang, label: 'Go'}
        ]
    },
    {
        heading: 'Smart Contract Testing Frameworks',
        values: [
            {icon: Truffle, label: 'Truffle'},
            {icon: Hardhat, label: 'Hardhat'},
            {icon: brownie, label: 'Brownie'},
            {icon: waffle, label: 'Waffle'},
            {icon: Foundry, label: 'Foundry'},
            {icon: embark, label: 'Embark'}
        ]
    },
    {
        heading: 'Front-End Languages',
        values: [
            {icon: html5, label: 'HTML5'},
            {icon: css3, label: 'CSS'},
            {icon: javascript, label: 'JavaScript'},
            {icon: dart, label: 'Dart'},
            {icon: typescript, label: 'TypeScript'},
            {icon: kotlin, label: 'Kotlin'}
        ]
    },
    {
        heading: 'JavaScript Frameworks',
        values: [
            {icon: angular, label: 'Angular JS'},
            {icon: reactjs, label: 'React JS'},
            {icon: vuejs, label: 'Vue.js'},
            {icon: nextJs, label: 'Next.js'},
            {icon: svelte, label: 'Svelte'},
            {icon: nuxtjs, label: 'Nuxt.js'}
        ]
    },
    {
        heading: 'Back-End Languages',
        values: [
            {icon: java, label: 'Java'},
            {icon: python, label: 'Python'},
            {icon: nodejs, label: 'Node.js'},
            {icon: php, label: 'PHP'},
            {icon: ruby, label: 'Ruby'},
            {icon: goLang, label: 'Go'}
        ]
    },
    {
        heading: 'DevOps - Containerization',
        values: [
            {icon: docker, label: 'Docker'},
            {icon: kubernetes, label: 'Kubernetes'},
            {icon: redHat, label: 'Red Hat OpenShift'},
            {icon: ApacheMesos, label: 'Apache Mesos'},
            {icon: LXC, label: 'LXC'},
            {icon: Nomad, label: 'Nomad'}
        ]
    },
    {
        heading: 'DevOps - Automation',
        values: [
            {icon: ansible, label: 'Ansible'},
            {icon: puppet, label: 'Puppet'},
            {icon: Saltstack, label: 'Saltstack'},
            {icon: hashicrop, label: 'HashiCorp Terraform'},
            {icon: chef, label: 'Chef'},
            {icon: jenkins, label: 'Jenkins'}
        ]
    }
];
export const defiFinanceMobileProcess = [
    {
        heading: 'Idea & Conceptualization',
        description: 'We analyze your vision and market needs to define a clear roadmap and functional scope for your DeFi solution.'
    },
    {
        heading: 'Development',
        description:
            'Our developers code secure smart contracts and core features tailored to your platform’s use case and performance needs.'
    },
    {
        heading: 'Integration & Testing',
        description:
            'We integrate frontend/backend with smart contracts and rigorously test for functionality, performance, and user experience issues.'
    },
    {
        heading: 'Deployment',
        description:
            'We launch your DeFi platform on the selected blockchain, ensuring all modules and infrastructure are production-ready.'
    },
    {
        heading: 'Maintenance & Support',
        description: 'We monitor the platform post-launch, resolve issues promptly, and roll out performance or security updates as needed.'
    },
    {
        heading: 'Compliance & Auditing',
        description:
            'We audit smart contracts and workflows to ensure platform security, transparency, and compliance with evolving DeFi regulations.'
    }
];

const finance = cdnImage('white-label-crypto-exchage/Banking.webp');
const gaming = cdnImage('white-label-crypto-exchage/Gaming.webp');
const realEstate = cdnImage('white-label-crypto-exchage/Real Estate.webp');
const education = cdnImage('white-label-crypto-exchage/Education.webp');
const health = cdnImage('white-label-crypto-exchage/Healthcare.webp');
const insuranceImg = cdnImage('defi/Insurance.webp');
const commerce = cdnImage('white-label-crypto-exchage/E-commerce.webp');
const assest = cdnImage('defi/Asset Management.webp');
const payment = cdnImage('defi/Cross-Border Payments.webp');
export const keyIndustryDefiContent = [
    {
        src: finance,
        heading: 'Finance',
        redirect: '/fintech-software-development',
        body: 'Provide decentralized lending, borrowing, and asset management, enabling inclusive, efficient financial services without intermediaries, improving access and transparency globally.',
        content: [
            'Decentralized loans without traditional banking hurdles',
            'Peer-to-peer lending and borrowing systems',
            'Transparent portfolio and asset management dashboards',
            'Automated interest calculation and reward mechanisms',
            'Increased financial inclusivity and accessibility'
        ]
    },
    {
        src: gaming,
        heading: 'Gaming',
        redirect: '/blockchain-game-development',
        body: 'Enhance in-game economies with tokenized assets, NFTs, and decentralized marketplaces, offering secure trading and engaging experiences for players worldwide.',
        content: [
            'Secure trading of digital assets and NFTs',
            'Decentralized marketplaces for in-game purchases',
            'Token reward systems for player engagement',
            'Transparent ownership of digital collectibles',
            'Smart contract-based game mechanics for trust'
        ]
    },
    {
        src: realEstate,
        heading: 'Real-Estate',
        redirect: '/real-estate-software-development',
        body: 'Enable fractional property ownership, transparent blockchain transactions, and efficient property trading, making real estate investment more accessible, secure, and liquid.',

        content: [
            'Tokenized property shares for fractional investments',
            'Secure and verifiable ownership records',
            'Automated rental income distribution',
            'Efficient property trading without intermediaries',
            'Increased market liquidity for real estate assets'
        ]
    },
    {
        src: education,
        heading: 'Education',
        redirect: '/education-app-development-company',
        body: 'Streamline decentralized funding, scholarship distribution, and credential verification, improving trust, transparency, and access to global educational financial resources.',

        content: [
            'Decentralized scholarship and grant distribution',
            'Blockchain-based academic credential verification',
            'Transparent tuition and payment processing',
            'Global access to educational financial resources',
            'Reduced operational costs for institutions'
        ]
    },
    {
        src: health,
        heading: 'Healthcare',
        redirect: '/healthcare-app-development-company',
        body: 'Secure patient data management, automate insurance claims, and enhance transparency while giving patients control over their health records.',

        content: [
            'Blockchain-based health record management',
            'Automated insurance claim processing',
            'Patient-controlled data sharing with providers',
            'Enhanced transparency and auditability in records',
            'Faster insurance settlements and reduced fraud'
        ]
    },
    {
        src: insuranceImg,
        heading: 'Insurance',
        redirect: '',
        body: 'Implement smart contract-based policies and automated claims processing, reducing fraud, improving transparency, and accelerating settlement efficiency for policyholders.',

        content: [
            'Smart contract-based insurance policies',
            'Automated claims verification and settlement',
            'Fraud reduction through immutable records',
            'Transparent policy management dashboards',
            'Reduced administrative overhead for insurers'
        ]
    },
    {
        src: commerce,
        heading: 'E-Commerce',
        body: 'Integrate decentralized payment gateways and loyalty programs to provide secure transactions, increase engagement, and reduce costs for merchants and customers.',
        redirect: '',
        content: [
            'Secure, decentralized payment solutions',
            'Loyalty token systems for customer retention',
            'Lower transaction fees for merchants',
            'Transparent reward distribution mechanisms',
            'Seamless in-app and online transactions'
        ]
    },
    {
        src: assest,
        heading: 'Asset Management',
        body: 'Offer decentralized asset management platforms with secure, transparent, and efficient investment handling, reducing intermediaries and improving portfolio performance.',
        redirect: '',
        content: [
            'Decentralized portfolio management tools',
            'Automated investment tracking and reporting',
            'Transparent risk assessment and analytics',
            'Reduced reliance on intermediaries',
            'Optimized returns through algorithmic strategies'
        ]
    },
    {
        src: payment,
        heading: 'Cross-Border Payments',
        body: 'Streamline global payments using decentralized solutions, reducing fees, delays, and intermediaries while ensuring transparency, speed, and compliance for international transfers.',
        redirect: '',
        content: [
            'Instant global fund transfers with minimal fees',
            'Transparent transaction tracking on blockchain',
            'Automated currency conversion and settlement',
            'Compliance-friendly cross-border payment systems',
            'Streamlined operations for international businesses'
        ]
    }
];

const ndaxLogo = cdnImage('Blockchain-images/Ndax-partner-logo.webp');
const rvaLogo = cdnImage('Blockchain-images/RVA-partner-logo1.webp');
const ifincaLogo = cdnImage('Blockchain-images/Ifinica-partner-logo.webp');
const integraLogo = cdnImage('Blockchain-images/Integra-partner-logo.webp');
const okx = cdnImage('Blockchain-images/OKX-partner-logo.webp');
const prclogo = cdnImage('defi/PRc-coin-partner-logo.webp');
// Icons for Key Services
const consultingIcon = cdnImage('Blockchain-images/Blockchain-Consulting.webp');
const architectureIcon = cdnImage('defi/DeFi-Architecture-Prototyping.webp');
const appDevIcon = cdnImage('defi/DeFi-Application-Development.webp');
const securityIcon = cdnImage('defi/Security-Auditing-Compliance-Integration.webp');
const integrationIcon = cdnImage('defi/Third-Party-Cross-Chain-Integration.webp');
const supportIcon = cdnImage('defi/Support-Maintenance-Protocol-Evolution.webp');
const defiKeyImg = cdnImage('DeFi-images/defi-key-services.png');
export {defiKeyImg};

export const bannerBottomDefi = [
    {
        src: ndaxLogo,
        alt: 'NDAX'
    },
    {
        src: rvaLogo,
        alt: 'RVA'
    },
    {
        src: prclogo,
        alt: 'PRC'
    },
    {
        src: ifincaLogo,
        alt: 'iFinca'
    },
    {
        src: integraLogo,
        alt: 'Integra Ledger'
    },
    {
        src: okx,
        alt: 'OKX'
    },
    {
        src: ndaxLogo,
        alt: 'NDAX'
    },
    {
        src: rvaLogo,
        alt: 'RVA'
    },
    {
        src: prclogo,
        alt: 'PRC'
    },
    {
        src: ifincaLogo,
        alt: 'iFinca'
    },
    {
        src: integraLogo,
        alt: 'Integra Ledger'
    },
    {
        src: okx,
        alt: 'OKX'
    }
];

export const milestonesDataDefi = [
    {value: '11+', text: 'Years of Blockchain\nExpertise'},
    {value: '150+', text: 'Team Of Skilled\nEngineers'},
    {value: '$26.6M+', text: 'Raised by Our\nClients'},
    {value: '500+', text: 'Enterprise-Grade Solutions\nDelivered'},
    {value: '50+', text: 'DeFi & Digital Asset\nDeployments'}
];

export const decentralizedDefiDataPoints = [
    {heading: 'High Uptime', subheading: '99.9% availability for uninterrupted DeFi operations.'},
    {heading: 'Trusted Volume', subheading: '$50M+ securely processed through our platforms.'},
    {heading: '3–5 Week MVP Launch', subheading: 'Get your DeFi product to market in under 5 weeks.'},
    {heading: 'Low Gas Optimization', subheading: 'Smart contracts designed for efficient, cost-effective transactions.'}
];

export const ndaxCheck = [
    'Facilitated over $12 billion in traded volume',
    'Registered as a Money Service Business (MSB) with FINTRAC',
    'Trusted by over 40,000 active users across Canada'
];

export const lendMateCheck = [
    'Minimum 80% Reduction in Operating Costs',
    '100% Increase in Solution Flexibility & Robustness',
    'Achieved at least 65% Increase in Efficiency',
    '70% Decrease in Time to Fund'
];

export const debutdxCheck = [
    '100% Enhancement in Security Protocols',
    '90% Increase in Transaction Speed',
    'At least 75% Improvement in Trading Efficiency',
    '85% Reduction in Operational Complexity'
];

export const nexusCheck = [
    'Advanced Security Features: 100% Increase in Security Layers',
    'Unparalleled Transaction Throughput: 120% Increase in Transaction Volume',
    'Innovative NFT Tools: 85% Growth in Feature Set',
    'Sustainable Community Development: 90% User Retention Rate'
];

const uniswap1 = cdnImage('defi/Uniswap.webp');
const aave1 = cdnImage('defi/Aave.webp');
const maker = cdnImage('defi/Maker.webp');
const sushiSwap1 = cdnImage('defi/SushiSwap.webp');
const convexFinance = cdnImage('defi/Convex.webp');
const yearnFinance = cdnImage('defi/Yearn-Finance.webp');
const Compound = cdnImage('defi/Compound.webp');
const Polkaswap = cdnImage('defi/Polkaswap.webp');
const InstaDApp = cdnImage('defi/InstaDApp.webp');
const YouHodler = cdnImage('defi/YouHodler.webp');
const terra = cdnImage('defi/Terra.webp');
export const defiPartnershipData = [
    {
        src: uniswap1,
        alt: 'UniSwap',
        label: 'Uniswap'
    },
    {
        src: aave1,
        alt: 'Aave',
        label: 'Aave'
    },
    {
        src: maker,
        alt: 'Maker',
        label: 'Maker'
    },
    {
        src: sushiSwap1,
        alt: 'SushiSwap',
        label: 'SushiSwap'
    },
    {
        src: convexFinance,
        alt: 'Convex finance',
        label: 'Convex Finance'
    },
    {
        src: yearnFinance,
        alt: 'Yearn Finance',
        label: 'Yearn Finance'
    },
    {
        src: Compound,
        alt: 'Compound Finance',
        label: 'Compound Finance'
    },
    {
        src: Polkaswap,
        alt: 'Polkaswap',
        label: 'Polkaswap'
    },
    {
        src: InstaDApp,
        alt: 'InstaDApp',
        label: 'InstaDApp'
    },
    {
        src: YouHodler,
        alt: 'YouHodler',
        label: 'YouHodler'
    },
    {
        src: terra,
        alt: 'Terra',
        label: 'Terra'
    },
    {
        src: uniswap1,
        alt: 'UniSwap',
        label: 'Uniswap'
    },
    {
        src: aave1,
        alt: 'Aave',
        label: 'Aave'
    },
    {
        src: maker,
        alt: 'Maker',
        label: 'Maker'
    },
    {
        src: sushiSwap1,
        alt: 'SushiSwap',
        label: 'SushiSwap'
    },
    {
        src: convexFinance,
        alt: 'Convex finance',
        label: 'Convex Finance'
    },
    {
        src: yearnFinance,
        alt: 'Yearn Finance',
        label: 'Yearn Finance'
    },
    {
        src: Compound,
        alt: 'Compound Finance',
        label: 'Compound Finance'
    },
    {
        src: Polkaswap,
        alt: 'Polkaswap',
        label: 'Polkaswap'
    },
    {
        src: InstaDApp,
        alt: 'InstaDApp',
        label: 'InstaDApp'
    },
    {
        src: YouHodler,
        alt: 'YouHodler',
        label: 'YouHodler'
    },
    {
        src: terra,
        alt: 'Terra',
        label: 'Terra'
    }
];

export const decentralizedDefiChooseBody = [
    'From ambitious startups to established enterprises, Debut Infotech builds secure, scalable, and innovative DeFi platforms tailored to real business needs. As a trusted DeFi development company, we deliver end-to-end solutions—from conceptualization to deployment and post-launch support—ensuring your decentralized finance project excels in a competitive market.',
    'Our DeFi development services combine deep blockchain expertise, agile methodologies, and cutting-edge technology. Whether you need decentralized exchanges, wallets, lending platforms, or enterprise-grade solutions, we ensure each platform is robust, compliant, and aligned with your strategic goals.'
];

export const decentralizedDefiChooseContent = [
    {
        heading: ' Agile Development Approach',
        subheading:
            'We follow agile workflows to deliver DeFi solutions faster, with iterative improvements, flexibility, and full transparency throughout the development lifecycle.'
    },
    {
        heading: 'Expert DeFi Developers',
        subheading:
            'Our skilled developers leverage the latest blockchain technologies to implement state-of-the-art features, smart contracts, and secure platform architectures.'
    },
    {
        heading: 'Quick Deployment',
        subheading:
            'We optimize time-to-market, fully deploying platforms within 3–9 months so you can capture opportunities and establish market presence swiftly.'
    },
    {
        heading: 'Tailored Customization',
        subheading:
            'Every platform is crafted to align with your business objectives, providing bespoke functionalities and seamless integration across your operations.'
    },
    {
        heading: 'Enterprise-Grade Security',
        subheading:
            'We implement advanced encryption, multi-signature wallets, and routine audits, ensuring maximum protection for digital assets and platform integrity.'
    }
];

export const defiVsTradfiData = {
    heading: 'Decentralized Finance (DeFi) vs. Traditional Finance (TradFi): Key Differences',
    subheading:
        'Traditional financial infrastructure was built on centralized control, restricted access, and layered intermediation — inefficiencies that DeFi eliminates at the protocol level. Today, with the global DeFi market on track to expand from $23.92 million to nearly <a href="https://www.businesswire.com/news/home/20260128367212/en/Decentralized-Finance-Market-2035---Decentralized-Apps-Dominate-DeFi-Market-Expected-to-Lead-in-Growth---ResearchAndMarkets.com?utm_campaign=shareaholic&utm_medium=copy_link&utm_source=bookmark" target="_blank">$945.59 million by 2035</a> at a CAGR of 39.88%, the window for strategic entry is narrowing. Enterprises and investors that move now capture protocol ownership, liquidity advantages, and ecosystem influence — before mainstream adoption erases the first-mover edge.',
    columns: [
        {label: 'Parameter', id: 'parameter'},
        {label: 'Traditional Financial Infrastructure (TradFi)', id: 'tradfi'},
        {label: 'Decentralized Finance Infrastructure (DeFi)', id: 'defi'}
    ],
    rows: [
        {
            parameter: 'Intermediaries',
            tradfi: {
                badge: 'Centralized',
                text: 'Relies on banks, clearinghouses, custodians, and legal authorities at every transaction layer.'
            },
            defi: {
                badge: 'Eliminated',
                text: 'Eliminates intermediaries through self-executing smart contracts and decentralized protocols.'
            }
        },
        {
            parameter: 'Transaction Cost',
            tradfi: {
                badge: 'High',
                text: 'High and non-negotiable — fees are set by intermediaries and compounded across multiple processing layers.'
            },
            defi: {
                badge: 'Minimal',
                text: 'Minimal and market-determined — costs reflect only network computation, with no rent-seeking middlemen.'
            }
        },
        {
            parameter: 'Settlement Speed',
            tradfi: {
                badge: 'T+1 to T+3',
                text: 'Constrained by institutional operating hours, cross-border settlements typically require T+1 to T+3 cycles.'
            },
            defi: {
                badge: 'Near-instant',
                text: 'Protocol-driven and near-instantaneous — finality achieved within seconds, independent of geography or time zones.'
            }
        },
        {
            parameter: 'Access & Eligibility',
            tradfi: {
                badge: 'Permissioned',
                text: 'Subject to extensive pre-qualification, credit scoring, KYC/AML procedures, and jurisdictional restrictions.'
            },
            defi: {
                badge: 'Permissionless',
                text: 'Permissionless by design — accessible to any participant with a compatible wallet, regardless of geography or credit history.'
            }
        },
        {
            parameter: 'Asset Custody',
            tradfi: {
                badge: 'Custodial',
                text: 'Assets are held and controlled by financial institutions; users bear counterparty and custodial risk.'
            },
            defi: {
                badge: 'Non-custodial',
                text: 'Non-custodial — users retain full sovereignty over their assets through private key ownership.'
            }
        },
        {
            parameter: 'Transparency',
            tradfi: {
                badge: 'Opaque',
                text: 'Opaque — fee structures, fund flows, and operational logic are proprietary and largely inaccessible to end users.'
            },
            defi: {
                badge: 'Fully auditable',
                text: 'Fully auditable — all transactions, protocol rules, and smart contract logic are immutably recorded on a public ledger.'
            }
        },
        {
            parameter: 'Interoperability',
            tradfi: {
                badge: 'Fragmented',
                text: 'Fragmented — legacy infrastructure and proprietary systems create significant integration friction.'
            },
            defi: {
                badge: 'Composable',
                text: 'Composable by architecture — DeFi protocols interoperate natively across platforms and blockchain networks.'
            }
        },
        {
            parameter: 'Regulatory Framework',
            tradfi: {
                badge: 'Mature',
                text: 'Mature and well-defined — governed by established bodies including SEC, FINRA, FCA, and equivalent regional authorities.'
            },
            defi: {
                badge: 'Evolving',
                text: 'Evolving — regulatory clarity varies by jurisdiction; compliance obligations depend on protocol design and asset classification.'
            }
        },
        {
            parameter: 'Resilience & Downtime',
            tradfi: {
                badge: 'Vulnerable',
                text: 'Vulnerable to single points of failure, maintenance windows, and institutional insolvency.'
            },
            defi: {
                badge: 'Always on',
                text: 'Operated by distributed node networks — no central point of failure and continuous 24/7 availability.'
            }
        }
    ]
};

export const defiKeyServicesData = [
    {
        title: 'DeFi Consulting & Strategy',
        description:
            'We assess your market positioning, define a viable protocol architecture, map compliance obligations, and design tokenomics aligned with your business model. You receive an investment-grade technical roadmap that eliminates ambiguity and minimizes execution risk before development begins.',
        icon: consultingIcon
    },
    {
        title: 'DeFi Architecture & Prototyping',
        description:
            "We design a scalable, security-first system architecture and build a working proof-of-concept to validate your protocol's technical and commercial feasibility. You gain clarity on build complexity, cost exposure, and go-to-market sequencing before committing to full-scale development.",
        icon: architectureIcon
    },
    {
        title: 'DeFi Application Development',
        description:
            'Our engineers handle the complete DeFi application development lifecycle, covering smart contracts, front-end interfaces, back-end infrastructure, API layers, and oracle integrations. You receive a production-ready DeFi solution built to institutional performance and security standards.',
        icon: appDevIcon
    },
    {
        title: 'Security Auditing & Compliance Integration',
        description:
            'We conduct independent <a href="/smart-contract-audit-services" target="_blank">smart contract audits</a>, penetration testing, and vulnerability assessments alongside KYC/AML framework integration and regulatory compliance mapping across SEC, FINRA, GDPR, and MiCA. You go live with verified security and documented compliance coverage.',
        icon: securityIcon
    },
    {
        title: 'Third-Party & Cross-Chain Integration',
        description:
            'We integrate your DeFi protocol with enterprise systems, external data oracles, payment infrastructure, and cross-chain networks, ensuring seamless liquidity flow and operational continuity across <a href="/ethereum-development-services" target="_blank">Ethereum</a>, <a href="/solana-blockchain-development" target="_blank">Solana</a>, <a href="/polygon-blockchain-development" target="_blank">Polygon</a>, and BNB Chain.',
        icon: integrationIcon
    },
    {
        title: 'Support, Maintenance & Protocol Evolution',
        description:
            'We provide continuous L1–L3 post-deployment support, smart contract upgrade management, performance monitoring, and iterative feature development to keep your protocol competitive, compliant, and operationally resilient as the market evolves.',
        icon: supportIcon
    }
];

// Icons for Trends
const trendIcon1 = cdnImage('Blockchain-images/Blockchain-value.webp');
const trendIcon2 = cdnImage('Blockchain-images/Blockchain-Solutions.webp');
const trendIcon3 = cdnImage('Blockchain-images/Blockchain-for-Banking-Finance.webp');
const trendIcon4 = cdnImage('Blockchain-images/Blockchain-Infrastructure-Node-Engineering.webp');
export const defiTrendsKeyServicesData = [
    {
        title: 'Mordor Intelligence Projections',
        description: 'According to Mordor Intelligence, the DeFi market is expected to reach $78.47 billion by 2029.',
        icon: trendIcon1
    },
    {
        title: 'Grand View Research Forecast',
        description:
            'According to <a target="_blank" href="https://www.grandviewresearch.com/industry-analysis/decentralized-finance-market-report" rel="nofollow">Grand View Research</a>, the DeFi market is projected to reach $231.19 billion by 2030.',
        icon: trendIcon2
    },
    {
        title: 'Statista Market Engagement',
        description:
            '<a target="_blank" href="https://www.statista.com/outlook/fmo/digital-assets/defi/worldwide#revenue" rel="nofollow">Statista</a> also anticipates ARPU in DeFi to reach around USD 1,650 by 2027, signaling deeper individual engagement.',
        icon: trendIcon3
    },
    {
        title: 'Markets & Data Liquidity Patterns',
        description:
            '<a target="_blank" href="https://www.marketsandata.com/industry-reports/decentralized-finance-defi-market" rel="nofollow">Markets & Data</a> notes that by 2032, decentralized exchanges (DEXs) are projected to account for over 40% of total DeFi transaction volume, highlighting shifting on-chain liquidity patterns.',
        icon: trendIcon4
    }
];

export const defiExpertiseCTAData = {
    heading: 'Right Expertise. Right Architecture. Right Results.',
    subheading:
        "The gap between a DeFi platform that scales and one that gets exploited isn't luck — it's the engineering decisions made before a single line of code is written. We bring battle-tested smart contract architecture, liquidity-aware design, and end-to-end delivery to every protocol we build.",
    bullets: [
        'Smart contracts built to survive adversarial conditions',
        'Architecture designed for liquidity depth from day one',
        'Audit-ready code shipped on time, every time'
    ],
    buttonText: 'Launch With Confidence'
};

export const defiSolutionsData = [
    {
        title: '<a target="_blank" href="/decentralized-crypto-exchange-development">Decentralized Exchange (DEX) Platforms</a>',
        description:
            'Non-custodial trading infrastructure built for high-volume, permissionless token exchange across multiple blockchain networks. Our approach to DeFi exchange development focuses on deep liquidity, transparent pricing, and scalable protocol architecture.',
        points: [
            'AMM and order book protocol architecture',
            'Liquidity pool design and incentive modeling',
            'Multi-token swap engine with gas optimization',
            'On-chain governance and fee distribution modules'
        ],
        image: dexPlatforms
    },
    {
        title: 'DeFi Lending & Borrowing Platforms',
        description:
            'Collateralized lending infrastructure that enables permissionless credit access, algorithmic interest rate management, and automated liquidation without banks, credit checks, or intermediaries.',
        points: [
            'Collateral management and liquidation engine design',
            'Algorithmic interest rate modeling and <a href="/blog/what-is-a-blockchain-oracle" target="_blank">oracle integration</a>',
            'Flash loan architecture and under-collateralized lending frameworks',
            '<a href="/blog/blockchain-kyc" target="_blank">KYC</a>/<a href="/blog/anti-money-laundering-aml-in-cryptocurrency" target="_blank">AML</a>-integrated compliance layer for regulated environments'
        ],
        image: lendingPlatforms
    },
    {
        title: 'DeFi Staking & Restaking Platforms',
        description:
            'Proof-of-Stake infrastructure that enables token holders to earn protocol rewards through staking, liquid staking derivatives, and EigenLayer-compatible restaking mechanisms.',
        points: [
            'Validator onboarding, delegation logic, and reward distribution',
            'Liquid staking token issuance and redemption mechanism',
            'Restaking protocol integration with slashing protection modules',
            'Staking dashboard and governance participation interfaces'
        ],
        image: stakingPlatforms
    },
    {
        title: 'Decentralized Crowdfunding Platforms',
        description:
            'Blockchain-native fundraising infrastructure enabling transparent, token-based capital formation for projects, startups, and institutional issuers, aligned with DeFi token development strategies.',
        points: [
            'Smart contract-based milestone funding and escrow automation',
            'Token allocation, vesting schedule, and contributor wallet management',
            'On-chain governance for fund utilization and stakeholder voting',
            'IDO, ICO, and STO regulatory framework alignment'
        ],
        image: fundingPlatforms
    },
    {
        title: 'Yield Farming & Liquidity Protocol Platforms',
        description:
            'High-performance infrastructure supporting DeFi yield farming strategies, enabling users to optimize returns and contribute liquidity across decentralized markets.',
        points: [
            'Multi-protocol yield aggregator and auto-compounding strategy design',
            'Liquidity mining program and reward token emission scheduling',
            'Risk-tiered vault systems with smart contract capital allocation',
            'Real-time APY tracking and performance analytics interfaces'
        ],
        image: farmingPlatforms
    },
    {
        title: 'Decentralized Derivatives Trading Platforms',
        description:
            'On-chain derivatives infrastructure supporting perpetual contracts, options, and synthetic assets engineered for capital efficiency, institutional-grade risk management, and deep market liquidity.',
        points: [
            'Perpetual futures engine with funding rate and margin management',
            'Options protocol with on-chain settlement and expiry automation',
            'Synthetic asset issuance and collateralization ratio management',
            'Risk engine, liquidation infrastructure, and position monitoring'
        ],
        image: derivativesPlatforms
    },
    {
        title: '<a href="defi-wallet-development-company" target="_blank">DeFi Wallet Development</a>',
        description:
            '<a href="/blog/non-custodial-white-label-crypto-wallets-your-key-to-expanding-revenue-streams" target="_blank">Non-custodial</a>, multi-chain wallet infrastructure engineered for secure asset management, seamless DeFi protocol interaction, and institutional-grade key custody across web and mobile environments.',
        points: [
            '<a href="/blog/multi-chain-compatibility-in-ico-launchpad" target="_blank">Multi-chain</a> architecture supporting EVM, Solana, and Cosmos ecosystems',
            'Hardware wallet integration and multi-signature authorization',
            'In-wallet DeFi access for swapping, staking, lending, and yield farming',
            'Transaction simulation, gas estimation, and on-chain activity reporting'
        ],
        image: walletDev
    },
    {
        title: 'Cross-Chain Bridging Solutions',
        description:
            'Secure interoperability infrastructure that enables frictionless asset transfers, liquidity migration, and protocol connectivity across heterogeneous blockchain networks.',
        points: [
            'Lock-and-mint and burn-and-release bridge architecture',
            'Multi-validator consensus and fraud-proof security layer integration',
            'Cross-chain liquidity aggregation and routing optimization',
            'Bridge monitoring, anomaly detection, and incident response systems'
        ],
        image: bridgeSolutions
    },
    {
        title: '<a href="/real-world-asset-tokenization" target="_blank">Asset Tokenization Platforms</a>',
        description:
            'Regulated infrastructure for the issuance, management, and secondary trading of tokenized real-world assets built for private markets, institutional investors, and compliance-first environments.',
        points: [
            '<a href="real-estate-software-development" target="_blank">Real estate</a>, equity, bond, and commodity tokenization frameworks',
            'STO-compliant issuance and digital securities lifecycle management',
            'Investor onboarding, cap table automation, and ownership governance',
            'Secondary market integration and compliance reporting modules'
        ],
        image: assetTokenization
    },
    {
        title: 'DeFi Smart Contract Development & Auditing',
        description:
            'The programmable backbone of every DeFi protocol — engineered for security, efficiency, and audit readiness from the first line of code to mainnet deployment.',
        points: [
            'Custom smart contract architecture for lending, DEX, staking, and derivatives logic',
            'Formal verification, unit testing, and third-party audit preparation',
            'Gas optimisation and contract upgradeability pattern implementation',
            'Multi-signature governance, access control, and emergency pause mechanisms'
        ],
        image: auditSmartContract
    }
];

export const defiTestimonialData = [
    {
        name: 'Bilal Hammoud',
        discrption: `Working with Debut Infotech has been a game-changer for NDAX. Their unparalleled expertise in blockchain and fintech development allowed us to build a seamless, robust platform that enhances user experience and security. Their team's dedication, innovative approach, and attention to detail were evident throughout the project. Debut Infotech consistently exceeded our expectations, ensuring NDAX's position as a leading crypto trading platform. I highly recommend Debut Infotech to any enterprise seeking top-notch development services.`,
        profile: 'Founder of NDAX',
        src: cdnImage('defi/Bilal-Hammoud.webp'),
        isVideo: false,
        link: ''
    },
    {
        name: 'Haider Rafique',
        discrption: `Working with Debut Infotech on our white label crypto exchange was a great experience. Their team demonstrated strong expertise in blockchain, delivering a secure, scalable, and user-friendly platform tailored to our needs. Communication was transparent and responsive, and they ensured the project was completed on time with excellent post-launch support. I highly recommend their services for anyone looking to build a crypto exchange.`,
        profile: 'CMO, OKX',
        src: haiderImg,
        isVideo: false,
        link: 'https://www.linkedin.com/in/haider-rafique/'
    }
];

export const defiTopCardsData = [
    {
        image: elevenYear,
        title: 'Early Blockchain Adopters',
        description:
            'Active in blockchain development since 2015, contributing to early enterprise implementations and evolving alongside the ecosystem.'
    },
    {
        image: ibm1,
        title: 'IBM & Enterprise Blockchain Engagement',
        description:
            'Hands-on participation in IBM blockchain initiatives with exposure to Hyperledger Fabric, Corda frameworks, and regulated enterprise network models.'
    },
    {
        image: fabdep,
        title: 'Creators of FabDep (Fabric Deployer)',
        description:
            'Developed FabDep, an internal framework that streamlines Hyperledger Fabric deployment and reduces enterprise network setup complexity.'
    }
];

export const defiChecklistData = [
    {
        title: 'Proven Experience Since 2015',
        description: `We have been delivering <a href="/blockchain-development-services" target="_blank">blockchain development solutions</a> since 2015, building smart contracts, token ecosystems, decentralized exchanges & platforms operating in live environments. This sustained experience strengthens architecture planning, threat anticipation, and upgrade governance, ensuring DeFi platforms remain stable, adaptable, and production-ready over time.`,
        isList: false,
        footer: ''
    },
    {
        title: 'Deep Protocol Engineering Expertise',
        description: `Our teams operate at the smart contract and protocol layer, designing liquidity models, governance logic, and tokenomics structures aligned with sustainable incentives. We follow secure SDLC practices, structured code reviews, deterministic testing, and deployment controls to ensure consistent execution across complex decentralized infrastructures.`,
        isList: false,
        footer: ''
    },
    {
        title: 'Compliance-Aligned Development Practices',
        description: `We design DeFi systems with awareness of SOX, HIPAA, SEC regulatory guidance, CCPA, and GDPR requirements where applicable. Our frameworks incorporate audit readiness, structured reporting mechanisms, data protection controls, and traceable transaction records, supporting enterprises operating within regulated financial and data environments.`,
        isList: false,
        footer: ''
    },
    {
        title: 'Advanced Cryptographic Foundations',
        description: `Security is enforced through established cryptographic algorithms, including SHA-1, SHA-2 with SHA-256, SHA-3, and MD5 for legacy interoperability. We implement hashing mechanisms, digital signatures, encryption protocols, and secure key management practices to maintain transaction integrity and strengthen distributed trust models.`,
        isList: false,
        footer: ''
    },
    {
        title: 'Embedded Risk Mitigation Controls',
        description: `Rather than relying solely on external audits, we integrate risk mitigation strategies directly into protocol architecture. This includes oracle dependency assessments, flash-loan exploit safeguards, upgrade-safe contract structures, and treasury control frameworks, reducing exposure to operational and financial vulnerabilities.`,
        isList: false,
        footer: ''
    }
];

export const defiProtocolCTAData = {
    heading: 'Your DeFi Protocol Deserves More Than Generic Development.',
    subheading:
        'The difference between a protocol that captures market share and one that stalls at deployment comes down to one decision — who builds it. Work with a team that brings protocol-grade expertise, security-first engineering, and institutional-level execution to every DeFi engagement.',
    bullets: [
        'Zero compromise on smart contract security and audit readiness',
        'Protocols engineered to attract and retain on-chain liquidity',
        'From concept to mainnet — delivered with precision'
    ],
    buttonText: 'Start Building With Experts',
    image: defiProtocolCtaImg
};

export const defiTechCategories = [
    {
        id: 'frameworks-networks',
        heading: 'Blockchain Frameworks & Networks',
        items: [
            {label: 'Ethereum', src: ethereum},
            {label: 'BNB Chain', src: binance},
            {label: 'Solana', src: solana},
            {label: 'Polygon', src: polygon1},
            {label: 'Avalanche', src: avalanche},
            {label: 'Polkadot', src: polkadotLogo},
            {label: 'Arbitrum', src: arbitrum},
            {label: 'Optimism', src: optimism},
            {label: 'Base', src: baseStack}
        ]
    },
    {
        id: 'languages',
        heading: 'Smart Contract Programming Languages',
        items: [
            {label: 'Solidity', src: solidity},
            {label: 'Rust', src: rust},
            {label: 'Vyper', src: Vyper},
            {label: 'Move', src: Move},
            {label: 'Yul', src: Yul},
            {label: 'Cairo', src: Cairo},
            {label: 'Ink!', src: Ink}
        ]
    },
    {
        id: 'testing-frameworks',
        heading: 'Smart Contract Development & Testing Frameworks',
        items: [
            {label: 'Hardhat', src: Hardhat},
            {label: 'Truffle', src: Truffle},
            {label: 'Foundry', src: FoundryLogo},
            {label: 'Brownie', src: brownie},
            {label: 'Remix IDE', src: Remix},
            {label: 'Tenderly', src: Tenderly},
            {label: 'Ganache', src: Ganache}
        ]
    },
    {
        id: 'oracles',
        heading: 'Blockchain Oracles & Data Feeds',
        items: [
            {label: 'Chainlink', src: chainlink},
            {label: 'Band Protocol', src: Band},
            {label: 'API3', src: api3},
            {label: 'Pyth Network', src: pyth},
            {label: 'Tellor', src: Tellor}
        ]
    },
    {
        id: 'cloud-services',
        heading: 'Blockchain Cloud Services',
        items: [
            {label: 'AWS Blockchain', src: aws},
            {label: 'Microsoft Azure', src: azure},
            {label: 'Google Cloud Blockchain', src: googleCloud},
            {label: 'Chainstack', src: Chainstack},
            {label: 'Alchemy', src: Alchemy},
            {label: 'Infura', src: Infura},
            {label: 'Moralis', src: Moralis},
            {label: 'QuickNode', src: QuickNode}
        ]
    },
    {
        id: 'frontend',
        heading: 'Front-End Programming Languages & Frameworks',
        items: [
            {label: 'React.js', src: reactjs},
            {label: 'Next.js', src: nextJs},
            {label: 'Vue.js', src: vuejs},
            {label: 'Angular', src: angular},
            {label: 'TypeScript', src: typescript},
            {label: 'Web3.js', src: web3js},
            {label: 'Ethers.js', src: etherjs}
        ]
    },
    {
        id: 'storage-database',
        heading: 'Decentralized Storage & Database',
        items: [
            {label: 'IPFS', src: IPFS},
            {label: 'Filecoin', src: Filecoin},
            {label: 'Arweave', src: Arweave},
            {label: 'The Graph', src: theGraph},
            {label: 'Ceramic Network', src: Ceramic},
            {label: 'OrbitDB', src: OrbitDB}
        ]
    },
    {
        id: 'security-audit',
        heading: 'Security & Audit Tools',
        items: [
            {label: 'MythX', src: MythX},
            {label: 'Slither', src: Slither},
            {label: 'Echidna', src: Echidna},
            {label: 'OpenZeppelin', src: openZeppelin},
            {label: 'CertiK', src: Certik},
            {label: 'Manticore', src: Manticore}
        ]
    },
    {
        id: 'wallet-identity',
        heading: 'Wallet & Integration',
        items: [
            {label: 'MetaMask', src: metamask},
            {label: 'WalletConnect', src: WalletConnect},
            {label: 'Coinbase Wallet', src: Coinbase},
            {label: 'Gnosis Safe', src: Gnosis},
            {label: 'Web3Auth', src: Web3Auth},
            {label: 'Privy', src: Privy},
            {label: 'RainbowKit', src: RainbowKit}
        ]
    }
];

export const defiComplianceData = [
    {title: 'Built to Support KYC / AML Integration', icon: kycAml},
    {title: 'Permissioned Pools for Controlled Institutional Access', icon: permissionedPools},
    {
        title: '<a href="/blog/dao-governance-model" target="_blank">DAO Governance</a> Designed for Legal Wrapper Compatibility',
        icon: daoLegalWrapper
    },
    {title: 'Token Models Aligned with Regulatory Expectations', icon: tokenModelsRegulatory},
    {title: 'Multi-Jurisdiction Deployment Preparedness', icon: multiJurisdiction},
    {title: 'Governance & Treasury Controls Supporting Compliance Needs', icon: govTreasury},
    {title: 'Data Handling Aligned with GDPR, CCPA & Privacy Regulations', icon: gdprCcpa},
    {title: 'Audit Trails Designed for SOX & Financial Reporting Requirements', icon: auditSox},
    {title: 'Role-Based Access and Multi-Signature Controls', icon: roleBaseAccess},
    {title: 'Real-Time Monitoring and Anomaly Detection Support', icon: monitoringAnomaly}
];

export const defiProcessData = [
    {
        step: 'Step 1: We Start With the Business Model, Not the Blockchain',
        content: [
            'Before selecting a protocol or token standard, we break down how value moves inside your ecosystem.',
            '• Who exchanges value, and under what conditions?',
            '• Where does reconciliation fail or create operational drag?',
            '• Which compliance obligations govern your user base and jurisdiction?',
            '• What financial logic needs to be automated, and what should remain off-chain?',
            'We also define tokenomics modeling, incentive design, liquidity bootstrapping strategy, and core risk parameters such as collateral ratios, liquidation thresholds, and volatility buffers.',
            'This phase often surfaces that the core challenge is not the absence of blockchain, but fragmented coordination across systems, unclear token utility, misaligned incentives, or unresolved compliance exposure. That clarity shapes every architectural decision that follows.'
        ]
    },
    {
        step: 'Step 2: We Define the Regulatory and Compliance Perimeter',
        content: [
            'DeFi operates across jurisdictions with overlapping and rapidly evolving regulatory frameworks. Before architecture begins, we map the full compliance landscape relevant to your solution.',
            '• Which SEC regulations apply to your token structure or exchange mechanism?',
            '• Do KYC and AML obligations apply at the protocol or application layer?',
            '• Are GDPR, CCPA, or financial data residency requirements relevant to your user base?',
            '• Does your platform require SOX-aligned audit trails or HIPAA-adjacent data handling?',
            'We also define governance controls, including multi-signature treasury structures, timelock mechanisms, and role-based access policies to align protocol operations with regulatory accountability expectations.',
            'Compliance is not retrofitted at launch. It is embedded into smart contract logic, data architecture, access control design, and treasury governance from the outset.'
        ]
    },
    {
        step: 'Step 3: We Validate the Opportunity Before Writing a Line of Code',
        content: [
            '• Once business intent and compliance boundaries are clear, we validate whether the DeFi opportunity justifies the build. We deliver a structured business case that covers the following.',
            '• Competitor and protocol landscape analysis',
            '• Projected development costs and timeline',
            '• ROI and NPV modeling',
            '• Token economic modeling, incentive emission schedules, and liquidity strategy design',
            '• A quantified risk register covering operational, adoption, liquidity, smart contract, and regulatory dimensions',
            'This gives you and your stakeholders a defensible foundation before any capital is committed to development.'
        ]
    },
    {
        step: 'Step 4: We Architect the Protocol and Define the Roadmap',
        content: [
            'Architecture in DeFi is where the most consequential decisions are made. At this stage, we establish the following.',
            '• Optimal <a href="/blog/private-blockchain-network" target="_blank">blockchain network</a> selection based on throughput, cost, and composability requirements',
            '• Smart contract architecture using modular design patterns and upgradeable proxy frameworks (UUPS or Transparent)',
            '• Gas optimization strategy and formalized testing standards targeting >90% unit and integration coverage',
            '• Data flow mapping between on-chain and off-chain components',
            '• Oracle integration strategy for external data dependency management',
            '• Full project roadmap with milestones, team composition, KPIs, and cost breakdown by stage',
            '• Risk mitigation plan covering technical, operational, financial parameters, and market-side exposures'
        ]
    },
    {
        step: 'Step 5: We Design for the User Before We Build for the Protocol',
        content: [
            'A technically sound DeFi protocol that users cannot navigate confidently will not achieve adoption.',
            '• Wallet connection and authentication flows',
            '• Liquidity provision and position management interfaces',
            '• Transaction confirmation and gas cost visibility',
            '• Error recovery and failure state communication',
            '• Multi-wallet compatibility and cross-device responsiveness',
            'We design with DeFi-native user behavior and governance transparency in mind, ensuring treasury controls, timelocks, and upgrade mechanisms are clearly communicated in the interface.'
        ]
    },
    {
        step: 'Step 6: We Develop With Quality Running in Parallel, Not After',
        content: [
            'Development is not a hand-off from build to test. Quality assurance runs in parallel within a CI/CD automation environment. We concurrently deliver:',
            '• Front-end interfaces built for DeFi-native user interactions',
            '• Back-end infrastructure for off-chain data processing and system coordination',
            '• Smart contracts developed using modular architecture, gas-optimized patterns, and upgradeable proxy structures',
            '• Unit, integration, and fuzz testing targeting >90% coverage against formal specifications',
            '• Custom Oracle development for reliable external data feeds',
            '• APIs enabling third-party system integration',
            'Full integrations between application components verified before staging deployment'
        ]
    },
    {
        step: 'Step 7: We Audit the Smart Contracts Before They Are Immutable',
        content: [
            '• Before mainnet deployment, every contract undergoes a rigorous, multi-layered security process.',
            '• Formal pre-audit code freeze and internal security audit',
            '• Automated vulnerability detection using Slither, MythX, and Echidna',
            '• Manual code review by senior smart contract security engineers',
            '• Formal verification where protocol value or complexity warrants mathematical assurance',
            '• External third-party audit coordination for high-value protocols',
            '• Bug bounty program structuring prior to or immediately following mainnet launch',
            'All findings are fully remediated and re-verified before deployment proceeds.'
        ]
    },
    {
        step: 'Step 8: We Deploy to Production With Infrastructure Security in Place',
        content: [
            'Deployment is a controlled, security-first transition. Before go-live, we establish:',
            '• Multi-signature treasury setup and timelock-governed upgrade controls',
            '• Firewalls, intrusion detection, and prevention systems',
            '• SIEM integration for real-time security event monitoring',
            '• Intelligent on-chain fraud detection mechanisms',
            '• API authorization controls and rate limiting',
            '• Defined incident response playbooks and escalation procedures',
            '• Compliance verification procedures aligned to applicable regulatory requirements',
            '• User onboarding materials and system administrator documentation'
        ]
    },
    {
        step: 'Step 9: We Optimize Continuously After Launch',
        content: [
            'Our post-launch engagement is built around continuous improvement.',
            '• Real-time monitoring of protocol performance, treasury movements, and on-chain activity',
            '• Governance oversight of timelock changes and multi-sig operations',
            '• Regular security and compliance audits on defined review cycles',
            '• Infrastructure scaling to support transaction growth',
            '• Protocol upgrades, additional asset support, and expanded DeFi service layers',
            '• Cross-chain expansion, liquidity rebalancing strategies, and governance mechanism evolution'
        ]
    }
];

export const defiCostData = {
    heading: 'DeFi App Development Cost & Timeline',
    subheading:
        'Organizations evaluating DeFi application development cost for a decentralized finance initiative — whether a lending protocol, yield optimization platform, or liquidity infrastructure — must account for several critical investment drivers: tokenomics architecture, protocol business logic, on-chain governance frameworks, and cross-chain interoperability requirements.<br /><br />For teams looking to validate their DeFi concept before full-scale development, an MVP build focused on core protocol functionality, basic smart contract logic, and a functional user interface typically ranges from:',
    rangeText: 'Most MVP projects range from:',
    rangeValue: '$15,000 – $80,000+',
    rangeSubtext:
        'MVP scope, timeline, and cost vary based on the complexity of the underlying protocol, the number of smart contracts required, the chosen blockchain network, and the degree of compliance integration needed from day one. Teams with a clearly defined use case and well-scoped requirements tend to move from concept to testnet deployment within 8 to 16 weeks.',
    accordions: [
        {
            title: 'What Drives DeFi Development Cost?',
            type: 'bullets',
            introText: 'The total cost of a DeFi platform is influenced by:',
            items: [
                {title: 'Protocol type (lending, DEX, yield optimization, staking, on-chain derivatives, or synthetic asset issuance)'},
                {title: 'Smart contract logic depth and audit requirements'},
                {title: 'Chosen settlement layer (Ethereum, Solana, Avalanche, Arbitrum, Base, etc.)'},
                {title: 'Tokenomics architecture and on-chain governance design'},
                {title: 'Integration with oracle networks, KYC/AML, and cross-chain bridge infrastructure'},
                {title: 'Security validation, formal verification, and bug bounty program scope'}
            ]
        },
        {
            title: 'Estimated Cost by DeFi Product Type',
            type: 'projectTable',
            items: [
                {type: 'Staking & Yield Contracts', cost: '$20,000 – $50,000', timeline: '3–6 weeks'},
                {type: 'DEX / AMM Protocol', cost: '$80,000 – $200,000', timeline: '3–5 months'},
                {type: 'Lending & Borrowing Protocol', cost: '$150,000 – $400,000', timeline: '5–8 months'},
                {type: 'Derivatives / Synthetic Platform', cost: '$250,000 – $600,000', timeline: '6–10 months'},
                {type: 'Institutional DeFi Infrastructure', cost: '$500,000 – $2.5M+', timeline: '10–18 months'}
            ]
        },
        {
            title: 'How Protocol Complexity Impacts DeFi Cost?',
            type: 'timelineBullets',
            introText: 'Timeline and protocol scope advance in parallel.',
            items: [
                {label: 'Single-function staking contract', time: '3–5 weeks'},
                {label: 'AMM with liquidity pools and tiered fee structures', time: '3–5 months'},
                {label: 'Lending protocol with dynamic interest rate models and liquidation bots', time: '5–8 months'},
                {
                    label: 'Full-scale DeFi ecosystem with governance, vault strategies, and cross-chain bridges',
                    time: '10–18 months'
                },
                {
                    label: '<br />Compressed timelines without sufficient audit coverage remain the leading cause of protocol exploits — and post-breach recovery costs consistently outweigh the savings',
                    time: ''
                }
            ]
        },
        {
            title: 'Blockchain Network Selection & DeFi Cost Implications',
            type: 'platformBullets',
            introText: 'Your choice of settlement layer directly determines engineering complexity and protocol viability:',
            items: [
                {
                    platform: 'Ethereum',
                    impact: 'Deepest DeFi liquidity and composability; demands rigorous gas optimization and audit investment'
                },
                {
                    platform: 'Arbitrum / Optimism',
                    impact: 'Layer 2 efficiency with EVM compatibility; reduces deployment cost without compromising security'
                },
                {
                    platform: 'Solana',
                    impact: 'High-throughput execution for latency-sensitive DeFi; requires specialized Rust and Anchor expertise'
                },
                {
                    platform: 'Avalanche',
                    impact: 'Subnet architecture suited for isolated, application-specific DeFi ecosystems'
                },
                {
                    platform: 'Base',
                    impact: 'Coinbase-backed Layer 2 with a growing DeFi ecosystem and competitive development economics'
                },
                {
                    platform:
                        'Selecting an incompatible settlement layer limits liquidity access, protocol composability, and long-term ecosystem scalability',
                    impact: ''
                }
            ]
        }
    ]
};

export const defiFinalCTAData = {
    heading: 'Ready to Build Your DeFi Solution?',
    subheading:
        'Every DeFi project carries its own protocol complexity, compliance perimeter, and go-to-market timeline. The fastest way to understand your development scope and cost is to align your business model, regulatory obligations, and technical architecture upfront.',
    buttonText: 'Get a Free DeFi Consultation',
    image: defiFinalCtaGraphic
};
